import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  FunctionField,
  TopToolbar,
  CreateButton,
  useRefresh,
  FilterButton,
  SelectInput,
  DateInput,
  BooleanInput,
  useListContext,
  useNotify,
} from 'react-admin';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Button as MuiButton,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import { PageTitle } from '../../components/PageTitle';
import {
  Edit as EditIcon,
  NotificationImportant as NotificationIcon,
  Add as AddIcon,
  MarkEmailRead as ReadIcon,
  MarkEmailUnread as UnreadIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  LocalShipping as VehicleIcon,
} from '@mui/icons-material';
import { NotificationDialog } from '../../components/dialogs/NotificationDialog';
import { tenantService } from '../../services/tenantService';

// Custom fields for notifications
const SubjectField = ({ source }: { source: string }) => (
  <FunctionField
    source={source}
    render={(record: any) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography 
          variant="body2" 
          fontWeight={record.isRead ? 'normal' : 'bold'}
          color={record.isRead ? 'text.secondary' : 'text.primary'}
        >
          {record.subject}
        </Typography>
        {!record.isRead && (
          <Chip
            label="NEW"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        )}
      </Box>
    )}
  />
);

const MessageField = ({ source }: { source: string }) => (
  <FunctionField
    source={source}
    render={(record: any) => (
      <Box>
        <Typography 
          variant="body2" 
          color={record.isRead ? 'text.secondary' : 'text.primary'}
          sx={{ 
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 300,
          }}
        >
          {record.message}
        </Typography>
      </Box>
    )}
  />
);

const UserField = ({ source }: { source: string }) => (
  <FunctionField
    source={source}
    render={(record: any) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="body2">
          {record.user ? record.user.name : 'All Users'}
        </Typography>
      </Box>
    )}
  />
);

const EquipmentField = ({ source }: { source: string }) => (
  <FunctionField
    source={source}
    render={(record: any) => (
      record.equipment ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VehicleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2">
            {record.equipment.name}
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      )
    )}
  />
);

const StatusField = ({ source }: { source: string }) => (
  <FunctionField
    source={source}
    render={(record: any) => (
      <Chip
        label={record.isRead ? 'Read' : 'Unread'}
        size="small"
        color={record.isRead ? 'default' : 'primary'}
        variant={record.isRead ? 'outlined' : 'filled'}
        icon={record.isRead ? <ReadIcon sx={{ fontSize: 14 }} /> : <UnreadIcon sx={{ fontSize: 14 }} />}
      />
    )}
  />
);

const TypeField = ({ source }: { source: string }) => (
  <FunctionField
    source={source}
    render={(record: any) => (
      <Chip
        label={record.isSystem ? 'System' : 'Manual'}
        size="small"
        color={record.isSystem ? 'info' : 'warning'}
        variant="outlined"
      />
    )}
  />
);

// Actions for each notification
const NotificationActions = ({ record }: { record: any }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const refresh = useRefresh();
  const notify = useNotify();

  const handleToggleRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantCode = tenantService.getCurrentTenantCode();

      const endpoint = record.isRead ? 'mark-unread' : 'mark-read';
      const response = await fetch(`${baseUrl}/notifications/${record.id}/${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'x-tenant-code': tenantCode,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to update notification status');
      }

      refresh();
      notify(`Notification marked as ${record.isRead ? 'unread' : 'read'}`, { type: 'success' });
    } catch (error) {
      console.error('Error toggling notification status:', error);
      notify('Failed to update notification status', { type: 'error' });
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantCode = tenantService.getCurrentTenantCode();

      const response = await fetch(`${baseUrl}/notifications/${record.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'x-tenant-code': tenantCode,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete notification');
      }

      refresh();
      notify('Notification deleted successfully', { type: 'success' });
    } catch (error) {
      console.error('Error deleting notification:', error);
      notify('Failed to delete notification', { type: 'error' });
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          setEditDialogOpen(true);
        }}
        size="small"
        color="primary"
        title="Edit notification"
      >
        <EditIcon />
      </IconButton>
      
      <IconButton
        onClick={handleToggleRead}
        size="small"
        color={record.isRead ? 'default' : 'primary'}
        title={record.isRead ? 'Mark as unread' : 'Mark as read'}
      >
        {record.isRead ? <UnreadIcon /> : <ReadIcon />}
      </IconButton>

      <IconButton
        onClick={handleDelete}
        size="small"
        color="error"
        title="Delete notification"
      >
        <DeleteIcon />
      </IconButton>

      <NotificationDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        recordId={record.id}
        isEdit={true}
      />
    </Box>
  );
};

// Summary cards component
const NotificationsSummary = () => {
  const { data = [] } = useListContext();
  
  const summaryData = React.useMemo(() => {
    const total = data.length;
    const unread = data.filter((n: any) => !n.isRead).length;
    const read = data.filter((n: any) => n.isRead).length;
    const system = data.filter((n: any) => n.isSystem).length;
    const manual = data.filter((n: any) => !n.isSystem).length;

    return { total, unread, read, system, manual };
  }, [data]);

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', pb: 2 }}>
              <Typography variant="h4" color="primary" gutterBottom>
                {summaryData.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Notifications
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', pb: 2 }}>
              <Typography variant="h4" color="error" gutterBottom>
                {summaryData.unread}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unread
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', pb: 2 }}>
              <Typography variant="h4" color="success.main" gutterBottom>
                {summaryData.read}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Read
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', pb: 2 }}>
              <Typography variant="h4" color="info.main" gutterBottom>
                {summaryData.system}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                System
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', pb: 2 }}>
              <Typography variant="h4" color="warning.main" gutterBottom>
                {summaryData.manual}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manual
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Filters for the notifications list
const notificationFilters = [
  <SelectInput
    source="isRead"
    choices={[
      { id: 'false', name: 'Unread' },
      { id: 'true', name: 'Read' },
    ]}
    emptyText="All notifications"
  />,
  <SelectInput
    source="isSystem"
    choices={[
      { id: 'true', name: 'System' },
      { id: 'false', name: 'Manual' },
    ]}
    emptyText="All types"
  />,
  <DateInput source="createdAt_gte" label="Created From" />,
  <DateInput source="createdAt_lte" label="Created To" />,
];

// List actions
const NotificationsListActions = () => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const refresh = useRefresh();
  const notify = useNotify();

  const handleMarkAllRead = async () => {
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantCode = tenantService.getCurrentTenantCode();

      const response = await fetch(`${baseUrl}/notifications/mark-all-read`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'x-tenant-code': tenantCode,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }

      refresh();
      notify('All notifications marked as read', { type: 'success' });
    } catch (error) {
      console.error('Error marking all as read:', error);
      notify('Failed to mark all notifications as read', { type: 'error' });
    }
  };

  const handleDeleteRead = async () => {
    if (!window.confirm('Are you sure you want to delete all read notifications?')) {
      return;
    }

    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantCode = tenantService.getCurrentTenantCode();

      const response = await fetch(`${baseUrl}/notifications/read`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'x-tenant-code': tenantCode,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete read notifications');
      }

      refresh();
      notify('Read notifications deleted successfully', { type: 'success' });
    } catch (error) {
      console.error('Error deleting read notifications:', error);
      notify('Failed to delete read notifications', { type: 'error' });
    }
  };

  return (
    <TopToolbar>
      <FilterButton />
      <MuiButton
        onClick={handleMarkAllRead}
        startIcon={<ReadIcon />}
        variant="outlined"
        size="small"
        sx={{ mr: 1 }}
      >
        Mark All Read
      </MuiButton>
      <MuiButton
        onClick={handleDeleteRead}
        startIcon={<DeleteIcon />}
        variant="outlined"
        size="small"
        color="error"
        sx={{ mr: 1 }}
      >
        Delete Read
      </MuiButton>
      <CreateButton onClick={() => setCreateDialogOpen(true)} />
      <NotificationDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </TopToolbar>
  );
};

// Main notifications list component
export const NotificationsList = () => {
  return (
    <Box>
      <PageTitle
        title="Notifications"
        titleKey="pages.notifications"
        subtitle="System alerts and messages"
        icon={<NotificationIcon />}
      />
      <List
        actions={<NotificationsListActions />}
        filters={notificationFilters}
        sort={{ field: 'createdAt', order: 'DESC' }}
        sx={{ '& .RaList-main': { mt: 0 } }}
        empty={
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <NotificationIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No notifications yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              System notifications and manual messages will appear here
            </Typography>
            <MuiButton variant="contained" startIcon={<AddIcon />}>
              Create Your First Notification
            </MuiButton>
          </Box>
        }
      >
      <NotificationsSummary />
      <Divider sx={{ mb: 2 }} />
      <Datagrid
        bulkActionButtons={false}
        sx={{
          '& .RaDatagrid-row': {
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        }}
      >
        <SubjectField source="subject" label="Subject" />
        <MessageField source="message" label="Message" />
        <UserField source="user" label="Target User" />
        <EquipmentField source="equipment" label="Equipment" />
        <StatusField source="isRead" label="Status" />
        <TypeField source="isSystem" label="Type" />
        <DateField source="createdAt" label="Created" />
        <FunctionField
          label="Actions"
          render={(record: any) => <NotificationActions record={record} />}
        />
      </Datagrid>
    </List>
    </Box>
  );
};